/**
 * Nível H.264 MÍNIMO que comporta um quadro — calculado, não negociado.
 *
 * ## Por que calcular, e não perguntar ao navegador
 *
 * `VideoEncoder.isConfigSupported` **não valida o nível contra a resolução** no
 * Firefox: ele responde `supported: true` para qualquer nível que se peça, e o
 * encoder depois estampa aquele nível no SPS, do jeito que foi pedido. Medido,
 * com um quadro de 1918x904 (6840 macroblocos):
 *
 *   pedido L4.2 → Firefox emite avc1.42c02a  → a Meta recusa o arquivo
 *                 131053 "Media file scrutiny ... mediaEngineStatus: 0"
 *   pedido L3.1 → Firefox emite avc1.42c01f  → stream NÃO CONFORME
 *                 (L3.1 só comporta 3600 macroblocos)
 *   Chrome      → ignora o excesso e emite o nível que o quadro precisa (L4.0),
 *                 e esse arquivo a Meta aceita
 *
 * Ou seja: no Firefox quem escolhe o nível é quem pede. Então o nível tem de
 * sair de uma conta determinística sobre as dimensões, e o
 * `isConfigSupported` fica só como porteiro de capacidade — nunca como
 * selecionador.
 *
 * ## A tabela
 *
 * `MaxFS` é o tamanho máximo do quadro em macroblocos e `MaxMBPS` o máximo de
 * macroblocos por segundo, os dois da Tabela A-1 do padrão H.264. Os dois
 * limitam: um quadro pequeno a uma taxa alta também estoura o nível.
 */

export interface NivelH264 {
    nome: string;
    /** `level_idc`, que é o nível × 10 — é o byte que entra no codec string. */
    idc: number;
    maxFS: number;
    maxMBPS: number;
}

export const NIVEIS_H264: readonly NivelH264[] = [
    { nome: '3.0', idc: 0x1e, maxFS: 1620, maxMBPS: 40_500 },
    { nome: '3.1', idc: 0x1f, maxFS: 3600, maxMBPS: 108_000 },
    { nome: '3.2', idc: 0x20, maxFS: 5120, maxMBPS: 216_000 },
    { nome: '4.0', idc: 0x28, maxFS: 8192, maxMBPS: 245_760 },
    { nome: '4.2', idc: 0x2a, maxFS: 8704, maxMBPS: 522_240 },
    { nome: '5.0', idc: 0x32, maxFS: 22_080, maxMBPS: 589_824 },
    { nome: '5.1', idc: 0x33, maxFS: 36_864, maxMBPS: 983_040 },
];

/** Macroblocos de um quadro: 16x16 pixels cada, arredondando para cima. */
export function macroblocos(largura: number, altura: number): number {
    return Math.ceil(largura / 16) * Math.ceil(altura / 16);
}

/**
 * Menor nível que comporta `largura x altura` a `fps`, ou `null` quando nem o
 * maior da tabela serve (caso em que o ffmpeg.wasm assume — ele não tem esse
 * limite).
 */
export function nivelH264Minimo(
    largura: number,
    altura: number,
    fps = 30,
): NivelH264 | null {
    if (!(largura > 0) || !(altura > 0)) return null;
    const fs = macroblocos(largura, altura);
    const mbps = fs * Math.max(1, fps);
    return NIVEIS_H264.find((n) => fs <= n.maxFS && mbps <= n.maxMBPS) ?? null;
}

/**
 * Codec string do perfil **Baseline** no menor nível que serve.
 *
 * Baseline (`profile_idc` 66 = `0x42`, flags de constraint `0x00`) é o perfil
 * que a Meta aceita sem discussão.
 */
export function codecH264Minimo(
    largura: number,
    altura: number,
    fps = 30,
): string | null {
    const nivel = nivelH264Minimo(largura, altura, fps);
    if (!nivel) return null;
    return `avc1.4200${nivel.idc.toString(16).padStart(2, '0')}`;
}
